import {
  Box,
  Flex,
  Heading,
  Skeleton,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  finleyAssertUnreachable,
  getErrorMessage,
} from '@finley-technologies/common';

import {
  useDeactivateCdqConfigMutation,
  useGetActiveCdqConfigsQuery,
} from './cdq.gql.generated';
import { CollateralDataQualityCheckAddEditCdqDrawerContainer } from './CollateralDataQualityCheckAddEditCdqDrawerContainer';
import {
  CollateralDataQualityCheckAddMenu,
  CollateralDataQualityCheckNoConfigsDisplay,
  CollateralDataQualityCheckOverviewFilterBar,
} from './CollateralDataQualityCheckComponents';
import { CollateralDataQualityCheckDetails } from './CollateralDataQualityCheckDetails';
import { CollateralDataQualityCheckResult } from './CollateralDataQualityCheckResult';
import {
  CollateralDataQualityResults,
  CollateralDataQualitySortMode,
  CollateralDataQualityViewMode,
  placeholderSQLString,
} from './utils';
import { doesSearchQueryExistInString } from '~/packages/common-frontend/components/SearchAndCopyComponents';
import {
  errorToastConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';

type CollateralDataQualityCheckProps = {
  organizationId: string;
  receivablesRoutePrefix: string;
};
export const CollateralDataQualityCheck = ({
  organizationId,
  receivablesRoutePrefix,
}: CollateralDataQualityCheckProps) => {
  // !TODO @eldarshamukhmedov No idea why this isn't just using `./overview`, `./detail` subroutes
  const [viewMode, setViewMode] = useState<CollateralDataQualityViewMode>(
    'overview',
  );
  const [selectedQueryResults, setSelectedQueryResults] = useState<
    CollateralDataQualityResults | undefined
  >(undefined);

  // Search, Filter, Sort state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [selectedSortMode, setSelectedSortMode] = useState<
    CollateralDataQualitySortMode | undefined
  >();
  // @dani add passed / failed filter when persisting results is implemented

  // Add New CDQ sidedrawer state
  const {
    isOpen: isAddEditCdqCheckOpen,
    onOpen: onAddEditCdqCheckOpen,
    onClose: onAddEditCdqCheckClose,
  } = useDisclosure();
  const [addEditCdqCheckDrawerMode, setAddEditCdqCheckDrawerMode] = useState<
    'add' | 'edit'
  >('add');
  // const [selectedCdqConfig, setSelectedCdqConfig] = useState<
  //   ConfiguredCdqGQLType | undefined
  // >();

  const toast = useToast();
  const {
    data: cdqConfigData,
    loading: isCdqConfigDataLoading,
    refetch: refetchCdqConfigs,
  } = useGetActiveCdqConfigsQuery({
    variables: {
      organizationId,
    },
    fetchPolicy: 'network-only',
    onError: (err) => {
      const errorMessage = getErrorMessage(err);
      toast({
        ...errorToastConfig,
        title: `Error fetching collateral data quality checks: ${errorMessage}`,
      });
    },
  });

  const [
    deactivateCdqConfig,
    { loading: isDeactivateCdqConfigLoading },
  ] = useDeactivateCdqConfigMutation();

  // get all cdq name selection options from cdqConfigData
  const nameSelectOptionsAndValues: {
    options: {
      label: string;
      value: string;
    }[];
    values: string[];
  } | null = useMemo(() => {
    if (cdqConfigData?.getActiveCdqConfigs?.length) {
      const options: { label: string; value: string }[] = [];
      const values: string[] = [];

      cdqConfigData.getActiveCdqConfigs.forEach((cdqConfig) => {
        options.push({
          label: cdqConfig.name,
          value: cdqConfig.name,
        });
        values.push(cdqConfig.name);
      });
      return {
        options,
        values,
      };
    }
    return null;
  }, [cdqConfigData?.getActiveCdqConfigs]);

  // set initial selected cdq configs to all
  useEffect(() => {
    if (nameSelectOptionsAndValues?.values.length) {
      setSelectedNames(nameSelectOptionsAndValues.values);
    }
  }, [nameSelectOptionsAndValues?.values]);

  const filteredAndSortedCdqConfigsForSearchQuery = useMemo(() => {
    if (!cdqConfigData?.getActiveCdqConfigs?.length) {
      return [];
    }

    let filteredAndSortedConfigs = cdqConfigData.getActiveCdqConfigs;

    if (selectedNames.length) {
      filteredAndSortedConfigs = filteredAndSortedConfigs.filter((cdqConfig) =>
        selectedNames.includes(cdqConfig.name),
      );
    }

    // @dani add passed / failed filter when persisting results is implemented

    if (searchQuery) {
      filteredAndSortedConfigs = filteredAndSortedConfigs.filter((cdqConfig) =>
        doesSearchQueryExistInString({
          queryString: searchQuery,
          stringToEvaluate: cdqConfig.name,
        }),
      );
    }

    if (selectedSortMode) {
      switch (selectedSortMode) {
        case 'ascName':
          filteredAndSortedConfigs = _.orderBy(
            filteredAndSortedConfigs,
            'name',
            'asc',
          );
          break;
        case 'descName':
          filteredAndSortedConfigs = _.orderBy(
            filteredAndSortedConfigs,
            'name',
            'desc',
          );
          break;
        default:
          finleyAssertUnreachable(
            selectedSortMode,
            `Can only sort by existing modes. Received ${selectedSortMode}`,
          );
      }
    }

    return filteredAndSortedConfigs;
  }, [
    cdqConfigData?.getActiveCdqConfigs,
    searchQuery,
    selectedNames,
    selectedSortMode,
  ]);

  const handleArchive = useCallback(
    async ({ configId }: { configId: string }) => {
      const response = await deactivateCdqConfig({
        variables: {
          id: configId,
        },
      });

      const errors = response.data?.deactivateCdqConfig.errors;
      if (errors && errors.length) {
        toast({
          ...errorToastConfig,
          title: errors.join(', '),
        });
      } else {
        toast({
          ...successToastConfig,
          title: 'Successfully archived.',
        });
        refetchCdqConfigs();
      }
    },
    [deactivateCdqConfig, refetchCdqConfigs, toast],
  );

  const onClearAllFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedNames(nameSelectOptionsAndValues?.values ?? []);
    setSelectedSortMode(undefined);
  }, [nameSelectOptionsAndValues]);

  const onOpenAddCdqDrawer = useCallback(() => {
    setAddEditCdqCheckDrawerMode('add');
    // setSelectedCdqConfig(undefined);
    onAddEditCdqCheckOpen();
  }, [onAddEditCdqCheckOpen]);

  const onCloseAddEditCdqModal = useCallback(() => {
    onAddEditCdqCheckClose();
    // setSelectedCdqConfig(undefined);
  }, [onAddEditCdqCheckClose]);

  if (!cdqConfigData || isCdqConfigDataLoading) {
    // @dani add better skeleton
    return <Skeleton h="400px" />;
  }

  return (
    <Box>
      {viewMode === 'overview' && (
        <>
          <Flex alignItems="center">
            <Heading marginBottom="40px" display="inline-block">
              Collateral Data Quality Monitoring
            </Heading>
            <Spacer />
            <CollateralDataQualityCheckAddMenu
              onClickAddCdqCheck={onOpenAddCdqDrawer}
            />
          </Flex>
          <Tabs>
            <TabList>
              <Tab mt="0">All CDQ</Tab>
            </TabList>

            <TabPanels>
              <TabPanel px="0">
                <VStack spacing="16px" w="100%" alignItems="left" mt="24px">
                  <CollateralDataQualityCheckOverviewFilterBar
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    nameOptions={nameSelectOptionsAndValues?.options ?? []}
                    selectedNames={selectedNames}
                    setSelectedNames={setSelectedNames}
                    selectedSortMode={selectedSortMode}
                    setSelectedSortMode={setSelectedSortMode}
                    onClearAllFilters={onClearAllFilters}
                  />
                  {filteredAndSortedCdqConfigsForSearchQuery.map(
                    (cdqConfig) => {
                      const { id, name, decodedQuery } = cdqConfig;
                      return (
                        <CollateralDataQualityCheckResult
                          key={`cdq-result-${id}`}
                          cdqConfigId={id}
                          cdqConfigName={name}
                          cdqConfigQuery={decodedQuery}
                          organizationId={organizationId}
                          setViewMode={setViewMode}
                          setSelectedQueryResults={setSelectedQueryResults}
                          handleArchive={handleArchive}
                          isDeactivateCdqConfigLoading={
                            isDeactivateCdqConfigLoading
                          }
                        />
                      );
                    },
                  )}
                </VStack>

                {!filteredAndSortedCdqConfigsForSearchQuery.length && (
                  <CollateralDataQualityCheckNoConfigsDisplay
                    doConfigsExist={
                      !!cdqConfigData?.getActiveCdqConfigs?.length
                    }
                  />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
      {viewMode === 'detail' && selectedQueryResults && (
        <CollateralDataQualityCheckDetails
          queryResults={selectedQueryResults.queryResults}
          configName={selectedQueryResults.cdqConfigName}
          setViewMode={setViewMode}
          receivablesRoutePrefix={receivablesRoutePrefix}
        />
      )}

      {addEditCdqCheckDrawerMode === 'add' && (
        <CollateralDataQualityCheckAddEditCdqDrawerContainer
          creditFacilityId={undefined} // @dani update in the future to pass in cfid when we have cf-scoped cdq ui
          isOpen={isAddEditCdqCheckOpen}
          onClose={onCloseAddEditCdqModal}
          initialQuery={placeholderSQLString} // in add mode, pass in placeholder text. in edit mode, pass in existing when clicking an edit configured cdq (selectedCdqConfig)
          mode="add"
        />
      )}
    </Box>
  );
};
